import React, { FC, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { CalendarProps } from 'react-calendar';
import { BaseInputStyling, BlueButton, Box, Button, Input } from '~Styles';
import useI18n from '~Translations';
import { useButton, useClickOutside } from '~Hooks';

const Dropdown = styled.div`
  position: absolute;
  left: 0;
  top: 40px;
  margin-top: 0.5rem;
  width: 280px;
  background-color: white;
  border: 1px solid var(--colorBlueGray200);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 8px;
  border-radius: 4px;
  z-index: 10;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const OuterWrapper = styled.div`
  position: relative;
  width: 200px;
`;

const InputWrapper = styled.div<{ active: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: center;
  padding: 0 16px;
  height: 100%;

  span {
    color: ${({ active }) => (active ? 'var(--colorBlueGray1300)' : 'var(--colorBlueGray500)')};
    font-size: 14px;
  }

  ${BaseInputStyling}
`;

interface Props extends CalendarProps {
  amountRange: { from: number | null; to: number | null };
  setAmountRange: (range: { from: number | null; to: number | null }) => void;
}

const FromToInput: FC<Props> = ({ amountRange, setAmountRange }) => {
  const { i18n } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const [internalAmountRange, setInternalAmountRange] = useState<{
    from: number | null;
    to: number | null;
  }>({ from: null, to: null });
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setIsOpen(false));

  const clearButton = useButton({
    onClick: () => {
      setInternalAmountRange({ from: null, to: null });
      setAmountRange({ from: null, to: null });
      setIsOpen(false);
    },
  });

  const OKButton = useButton({
    onClick: () => {
      setAmountRange(internalAmountRange);
      setIsOpen(false);
    },
  });

  return (
    <OuterWrapper ref={ref}>
      <InputWrapper
        onTouchEnd={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setIsOpen(!isOpen);
        }}
        active={amountRange.from !== null || amountRange.to !== null}>
        {amountRange.from !== null && amountRange.to !== null ? (
          <span>{amountRange.from?.toString() + ' - ' + amountRange.to?.toString()}</span>
        ) : (
          <span>{i18n('FromToInput.SearchPlaceholder')}</span>
        )}
      </InputWrapper>
      {isOpen && (
        <Dropdown>
          <Input
            type="number"
            autoFocus
            placeholder={i18n('FromToInput.From')}
            value={internalAmountRange.from?.toString() ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setInternalAmountRange({
                to: internalAmountRange.to,
                from: +Number(e.target.value),
              })
            }
          />
          <Input
            type="number"
            placeholder={i18n('FromToInput.To')}
            value={internalAmountRange.to?.toString() ?? ''}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setInternalAmountRange({
                from: internalAmountRange.from,
                to: +Number(e.target.value),
              })
            }
          />
          <Box marginTop="medium" display="flex" gap="small">
            <Button {...clearButton}>{i18n('General.Clear')}</Button>
            <BlueButton {...OKButton}>{i18n('General.Submit')}</BlueButton>
          </Box>
        </Dropdown>
      )}
    </OuterWrapper>
  );
};

export default FromToInput;
